import { alpha, createTheme, type Theme } from "@mui/material/styles";
import { enUS as coreEnUS, frFR as coreFrFR } from "@mui/material/locale";
import {
  enUS as gridEnUS,
  frFR as gridFrFR,
} from "@mui/x-data-grid/locales";
import {
  enUS as chartsEnUS,
  frFR as chartsFrFR,
} from "@mui/x-charts/locales";

/**
 * Brand tokens carried over from the previous dashboard prototype. The blue is
 * the one fixed point — it is on the sign-in gradient, the logos and the
 * patient app (apps/mobile/src/theme/colors.ts blue[4]).
 */
export const brandBlue = "#3456FF";
const healthcareTeal = "#087F7A";
const ink = "#172033";

/**
 * A fifth semantic hue, for a fact that is neither an alarm nor ordinary
 * metadata: an elevated privilege (admin, superadmin) or a widened audience (a
 * public study). Those three badges used to be `warning`, which painted a
 * routine property of an account in the same colour as a missing consent.
 *
 * Violet because every other hue here is spoken for — blue is primary, teal is
 * secondary, green is success, red is error, amber is warning.
 */
const accentViolet = "#5B21B6";

declare module "@mui/material/styles" {
  interface Palette {
    accent: Palette["primary"];
  }
  interface PaletteOptions {
    accent?: PaletteOptions["primary"];
  }
}

const base = {
  cssVariables: true,
  palette: {
    mode: "light" as const,
    primary: {
      main: brandBlue,
      dark: "#2542D8",
      light: "#E8ECFF",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: healthcareTeal,
      dark: "#05645F",
      light: "#DDF5F2",
      contrastText: "#FFFFFF",
    },
    background: { default: "#F5F7FB", paper: "#FFFFFF" },
    text: { primary: ink, secondary: "#5D6678" },
    divider: "#E4E8F0",
    success: { main: "#18794E", light: "#E2F5EA" },
    // Burnt orange rather than the dark amber this was (#A35C00): every status
    // chip is drawn as its `light` tint carrying `main` as text, and that
    // amber read as brown once it was the text colour rather than a fill.
    warning: { main: "#9A3412", light: "#FFEDD5" },
    error: { main: "#B42318", light: "#FDE7E4" },
    accent: { main: accentViolet, light: "#EDE9FE", contrastText: "#FFFFFF" },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
      lineHeight: 1.2,
      fontWeight: 700,
      letterSpacing: "-0.035em",
    },
    h2: {
      fontSize: "1.35rem",
      lineHeight: 1.3,
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h3: { fontSize: "1.05rem", lineHeight: 1.35, fontWeight: 700 },
    // The px body scale from the original prototype's theme — the revamp theme
    // only defined h1–h3, leaving body text at MUI defaults.
    subtitle1: { fontSize: 16, fontWeight: 600, lineHeight: 1.5 },
    subtitle2: { fontSize: 14, fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: 16, lineHeight: 1.55 },
    body2: { fontSize: 14, lineHeight: 1.5 },
    caption: { fontSize: 12, lineHeight: 1.45 },
    button: { fontWeight: 650, textTransform: "none" as const },
  },
  shadows: [
    "none",
    "0 1px 2px rgba(23, 32, 51, 0.04), 0 8px 24px rgba(23, 32, 51, 0.05)",
    "0 4px 12px rgba(23, 32, 51, 0.08)",
    "0 8px 24px rgba(23, 32, 51, 0.10)",
    "0 12px 36px rgba(23, 32, 51, 0.12)",
    ...Array(20).fill("0 12px 36px rgba(23, 32, 51, 0.12)"),
  ] as Theme["shadows"],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { minWidth: 320, minHeight: "100%", scrollBehavior: "smooth" },
        body: { minWidth: 320, minHeight: "100%", margin: 0 },
        "#root": { minHeight: "100dvh" },
        "::selection": { backgroundColor: alpha(brandBlue, 0.16) },
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            scrollBehavior: "auto !important",
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      // 44px min height is both the touch-target floor and what keeps buttons
      // usable on a tablet. The 32px pill from the old prototype is reserved
      // for the sign-in CTA (applied locally) — it is far too large next to a
      // data grid.
      styleOverrides: {
        root: { minHeight: 44, borderRadius: 10, paddingInline: 18 },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 1 },
      styleOverrides: {
        root: { border: "1px solid", borderColor: "#E8EBF2" },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { minWidth: 44, minHeight: 44 } },
    },
    MuiTextField: { defaultProps: { size: "small" as const } },
    MuiTableCell: {
      styleOverrides: {
        head: { color: "#5D6678", fontWeight: 700, backgroundColor: "#F8F9FC" },
      },
    },
  },
};

const locales = {
  fr: [coreFrFR, gridFrFR, chartsFrFR],
  en: [coreEnUS, gridEnUS, chartsEnUS],
} as const;

/**
 * MUI ships its own translations for component chrome (pagination, grid
 * toolbar, date pickers). They are baked into the theme, so the theme has to
 * be rebuilt when the UI language changes — see Providers.
 */
export function buildTheme(language: string): Theme {
  const key = language.split("-")[0] === "en" ? "en" : "fr";
  return createTheme(base, ...locales[key]);
}

export const appTheme = buildTheme("fr");
